import * as React from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Rocket, Eye, Sparkles, ExternalLink } from 'lucide-react';

import { Button } from '@/components/ui/Button';
import { Card, CardContent } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { ConfirmDialog } from '@/components/data/ConfirmDialog';
import { AuditChip } from '@/components/audit/AuditChip';
import { useApiError } from '@/lib/useApiError';
import { useSetLaunchMode, useWaitlistMetrics } from '@/queries/phase2';
import {
  LAUNCH_MODES,
  type LaunchMode,
} from '@/schemas/admin/phase2/launchMode';
import { cn } from '@/lib/cn';

export const Route = createFileRoute('/_authed/phase2/launch-mode')({
  component: LaunchModeScreen,
});

const ICON: Record<LaunchMode, React.ComponentType<{ className?: string }>> = {
  'pre-launch': Eye,
  'soft-launch': Sparkles,
  live: Rocket,
};

function LaunchModeScreen() {
  const { t } = useTranslation('phase2');
  const resolveError = useApiError();
  // Reuse the metrics endpoint to read the current mode without inventing a
  // dedicated read; metrics returns `launch_mode` as part of its payload.
  const metrics = useWaitlistMetrics();
  const set = useSetLaunchMode();
  const [pendingMode, setPendingMode] = React.useState<LaunchMode | null>(null);

  const current = (metrics.data?.launch_mode ?? null) as LaunchMode | null;

  // Preview the consumer site so operators can verify how the launch-mode flag
  // is surfaced (or not, while the consumer hasn't wired `/v1/launch-mode` yet).
  // Sourced from VITE_CONSUMER_BASE_URL; falls back to localhost dev port.
  const consumerBase: string = (() => {
    const raw: unknown = import.meta.env.VITE_CONSUMER_BASE_URL;
    if (typeof raw === 'string' && raw && raw !== 'undefined') {
      return raw.replace(/\/+$/, '');
    }
    return 'http://localhost:3000';
  })();

  function startConfirm(mode: LaunchMode) {
    if (mode === current) return;
    setPendingMode(mode);
  }

  async function commit() {
    if (!pendingMode) return;
    try {
      const result = await set.mutateAsync({ mode: pendingMode });
      toast.success(t('launchMode.savedTitle', { defaultValue: 'Launch mode set.' }), {
        description: t('launchMode.savedBody', {
          defaultValue: 'Now: {mode}.',
          mode: result.mode,
        }),
      });
      setPendingMode(null);
    } catch (e) {
      const resolved = resolveError(e);
      toast.error(resolved.message, resolved.recovery ? { description: resolved.recovery } : undefined);
    }
  }

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div className="space-y-1">
          <h1 className="text-2xl font-semibold tracking-tight">
            {t('launchMode.title', { defaultValue: 'Launch mode' })}
          </h1>
          <p className="max-w-2xl text-sm text-[color:var(--color-fg-muted)]">
            {t('launchMode.subtitle', {
              defaultValue:
                "Sets a global flag that drives consumer-app behavior. Pre-launch hides social-proof widgets; soft-launch is the soft open; live is the public release.",
            })}
          </p>
        </div>
        <Button asChild variant="secondary" size="sm">
          <a href={consumerBase} target="_blank" rel="noreferrer">
            <ExternalLink className="size-4" aria-hidden />
            {t('launchMode.previewConsumer', { defaultValue: 'Preview consumer site' })}
          </a>
        </Button>
      </header>

      {current === 'pre-launch' ? (
        <div className="rounded-[var(--radius-md)] border border-[color:var(--color-warn)] bg-[color:var(--color-warn-soft)] p-4 text-sm">
          <p className="font-semibold">
            {t('launchMode.preLaunchBannerTitle', {
              defaultValue: 'Pre-launch is active.',
            })}
          </p>
          <p className="mt-1 text-[color:var(--color-fg)]">
            {t('launchMode.preLaunchBannerBody', {
              defaultValue:
                'Public clients hide social-proof widgets — no fabricated counts, online-now tickers, or activity pulses anywhere on Plustown. The admin shell honors the same rule.',
            })}
          </p>
        </div>
      ) : null}

      <div className="grid gap-4 lg:grid-cols-3">
        {LAUNCH_MODES.map((mode) => {
          const Icon = ICON[mode];
          const selected = current === mode;
          return (
            <Card
              key={mode}
              className={cn(
                'transition-shadow',
                selected && 'ring-2 ring-[color:var(--color-accent)]'
              )}
            >
              <CardContent className="space-y-4 pt-6">
                <div className="flex items-start justify-between gap-3">
                  <Icon className="size-6 text-[color:var(--color-accent)]" aria-hidden />
                  {selected ? (
                    <Badge tone="accent">
                      {t('launchMode.activeBadge', { defaultValue: 'Active' })}
                    </Badge>
                  ) : (
                    <Badge tone="neutral">{mode}</Badge>
                  )}
                </div>
                <div className="space-y-1">
                  <h2 className="text-lg font-semibold">
                    {t(`launchMode.modes.${mode}.title`, { defaultValue: mode })}
                  </h2>
                  <p className="text-sm text-[color:var(--color-fg-muted)]">
                    {t(`launchMode.modes.${mode}.body`, {
                      defaultValue:
                        mode === 'pre-launch'
                          ? 'Public clients hide social-proof widgets. No fabricated community counts, online-now tickers, or activity pulses.'
                          : mode === 'soft-launch'
                            ? 'Soft public open. Consumer features visible; ops still drives daily metrics manually.'
                            : 'Public release. Full consumer experience visible.',
                    })}
                  </p>
                </div>
                <div className="flex items-center justify-between gap-2">
                  <AuditChip />
                  <Button
                    onClick={() => startConfirm(mode)}
                    disabled={selected || set.isPending || metrics.isLoading}
                    variant={mode === 'live' ? 'primary' : 'secondary'}
                  >
                    {selected
                      ? t('launchMode.current', { defaultValue: 'Current' })
                      : t('launchMode.switchTo', {
                          defaultValue: 'Switch to {mode}',
                          mode,
                        })}
                  </Button>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <ConfirmDialog
        open={Boolean(pendingMode)}
        onOpenChange={(o) => !o && setPendingMode(null)}
        title={
          pendingMode
            ? t('launchMode.confirmTitle', {
                defaultValue: 'Switch launch mode to {mode}?',
                mode: pendingMode,
              })
            : ''
        }
        description={
          pendingMode
            ? t('launchMode.confirmBody', {
                defaultValue:
                  'This is global. All Plustown clients will respect the new mode within seconds. Type the mode name to continue.',
              })
            : ''
        }
        confirmLabel={t('launchMode.confirmCta', { defaultValue: 'Switch mode' })}
        requireType={pendingMode?.toUpperCase() ?? ''}
        loading={set.isPending}
        onConfirm={commit}
        destructive={pendingMode === 'live'}
      />
    </div>
  );
}
